/**
 * WHICH REFUND SLIP THE PRINTER IS ABOUT TO PRODUCE — the ORIGINAL, or a COPY
 * of one named refund (§5.7.2.1, trap 9).
 *
 * A pure function, and not a ternary inside `InvoicePage`, for the reason that
 * ternary shipped a bug nothing could catch: this app has no React test
 * renderer, so a decision living in a page component's render is a decision no
 * spec can reach. What shipped was that EVERY slip a guest was handed said
 * «صورة من إيصال المرتجع» / "Copy of the refund receipt" — because the only
 * control that fired the printer cleared the held original one line before
 * naming a copy, so the ORIGINAL branch was mounted on every refund and printed
 * on none. A tax document telling an accountant to go and find an original that
 * was never printed is exactly what §5.7.2.1's copy note exists to prevent, and
 * it said so about every refund in the store.
 *
 * So the rule is stated here, once, and tested:
 *
 * 1. **A copy explicitly asked for WINS**, even over a held original of the
 *    same refund. A cashier who taps a reference is asking for THAT slip; the
 *    printer must not hand them an unmarked original because the till happens
 *    to still be holding one. (This half was always right — it is the reason
 *    the reprint path clears the original, and it is kept.)
 * 2. **Otherwise the held ORIGINAL prints**, unmarked. It is reachable because
 *    a completed refund now fires the printer for itself, while the original is
 *    still in hand.
 * 3. **`NONE` when there is nothing honest to print** — no original held and
 *    either no copy asked for, or a copy asked for of a refund THIS READ OF THE
 *    BILL DOES NOT CARRY. The third case is not theoretical: a copy tapped
 *    against a stale read used to null the original, fail to resolve a reprint
 *    target, mount nothing, and still fire `window.print()` in refund scope —
 *    which is a blank sheet out of the roll on a till, and a cashier who
 *    believes a slip was printed.
 *
 * Deliberately takes IDS and not documents: the choice is about which refund,
 * and building either document needs the translator and the whole invoice.
 * Keeping those out is what makes this testable at all.
 */
export type RefundPaperChoice =
  /** Print `lastRefund` as it stands — never marked a duplicate. */
  | { kind: "ORIGINAL" }
  /** Rebuild this one refund from the bill and mark it a copy. */
  | { kind: "COPY"; commandId: string }
  /** Mount nothing, and do not fire the printer. */
  | { kind: "NONE" };

export function refundPaperChoice(state: {
  /**
   * The refund a cashier asked for a COPY of, or `null` for "none asked for".
   */
  reprintCommandId: string | null;
  /**
   * The command id of the ORIGINAL this device is still holding, or `null`.
   *
   * The id rather than the document, so this function cannot be given a
   * document and a mismatched id to disagree about.
   */
  originalCommandId: string | null;
  /**
   * The refund events the CURRENT read of the bill carries. A copy can only be
   * rebuilt from an event that is actually there.
   */
  knownCommandIds: readonly string[];
}): RefundPaperChoice {
  const { reprintCommandId, originalCommandId, knownCommandIds } = state;
  if (reprintCommandId !== null && knownCommandIds.includes(reprintCommandId)) {
    return { kind: "COPY", commandId: reprintCommandId };
  }
  if (originalCommandId !== null) return { kind: "ORIGINAL" };
  return { kind: "NONE" };
}
